import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Plus, LogOut } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, DISCORD_INVITE_URL, LOGO_URL } from "@/config";
import { userAvatarUrl } from "@/api/discordAssets";
import { useApi } from "@/hooks/useApi";
import { getCurrentUser, redirectToLogin, logout } from "@/api/auth";

const linkClass = ({ isActive }) =>
  `relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
    isActive ? "bg-white/10 text-paper-100" : "text-paper-400 hover:text-paper-100"
  }`;

const AuthButton = ({ mobile = false, onNavigate }) => {
  const { data: user } = useApi(getCurrentUser);
  if (user && !user.error) {
    return (
      <div className={`flex items-center gap-3 ${mobile ? "w-full" : ""}`} data-testid="user-menu">
        <Link
          to="/dashboard"
          data-testid="dashboard-link"
          className="flex items-center gap-3 rounded-full border border-white/10 py-1 pl-1 pr-4 transition-colors duration-300 hover:border-white/30"
        >
          <img
            src={userAvatarUrl(user) || LOGO_URL}
            alt={user.username || "Discord avatar"}
            width={32}
            height={32}
            decoding="async"
            className="h-8 w-8 rounded-full border border-white/15 object-cover"
          />
          {user.username && <span className="text-sm font-medium text-paper-200">{user.username}</span>}
        </Link>
        <button
          onClick={() => logout().finally(() => window.location.reload())}
          data-testid="logout-button"
          aria-label="Log out"
          className="rounded-full border border-white/15 p-2 text-paper-400 transition-colors duration-300 hover:border-white/40 hover:text-paper-100"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={() => {
        onNavigate?.();
        redirectToLogin();
      }}
      data-testid="login-button"
      className={`rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white/10 ${mobile ? "w-full" : ""}`}
    >
      Login with Discord
    </button>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-[65] transition-all duration-500 ${
        scrolled || open ? "glass border-b border-white/10" : "border-b border-transparent bg-black/85 backdrop-blur-md"
      }`}
      style={{ willChange: "transform" }}
      data-testid="navbar"
    >
      <nav className="mx-auto flex h-[72px] w-full max-w-7xl items-center justify-between px-6" aria-label="Main">
        <Logo />
        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => (
            <NavLink key={link.path} to={link.path} end={link.path === "/"} className={linkClass} data-testid={`nav-link-${link.label.toLowerCase()}`}>
              {link.label}
            </NavLink>
          ))}
        </div>
        <div className="hidden items-center gap-3 lg:flex">
          <AuthButton />
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="add-xevon-button"
            className="group inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.03]"
          >
            <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" aria-hidden="true" />
            Add Xevon
          </a>
        </div>
        <button
          className="rounded-full border border-white/15 p-2.5 text-paper-100 lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-button"
        >
          {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
        </button>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="glass overflow-hidden border-t border-white/10 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === "/"}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-base font-medium transition-colors duration-300 ${
                      isActive ? "bg-white/10 text-paper-100" : "text-paper-400 hover:bg-white/5 hover:text-paper-100"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <div className="mt-4 flex flex-col gap-3 border-t border-white/10 pt-5">
                <AuthButton mobile onNavigate={() => setOpen(false)} />
                <a
                  href={DISCORD_INVITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-add-xevon-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-black"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Add Xevon
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
